import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'StoreModel';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import Image from 'components/common/lazyImage';
import { PrimaryButton, StyledCardDescription } from 'components/styled-components/common';
import { useHistory } from 'react-router';
import AddCoachingModal from 'components/requests/add-coaching-modal';
import { Circle, Community, Project, Member, CommunityMembers } from 'redux/types/account';
import { fetchProjectMembers } from '../../services/api/project';
import { fetchCommunityMembers, fetchCommunityMembersByCircle } from '../../services/api/community';
import { createNewRequestAsync } from 'redux/actions/request';
import { fetchCommunityCirclesAsync } from '../../redux/actions/community';
import { StyledButton } from 'primitives/Button/style';

const Card = styled.div`
  width: 100%;
  min-height: 5em;
  border: 2px solid ${({ theme }) => theme.colors.grey.light};
  border-radius: 0.5em;
  padding: 1em;
  margin-bottom: 1em;

  display: flex;
  flex-direction: column;
  align-items: center;

  a,
  span {
    font-size: 0.9em;
  }
`;

const TAKE = 10;

const mapStateToProps = (state: RootState) => ({
  literals: state.literals,
  selectedCommunity: state.account.selectedCommunity,
});

const mapDispatchToProps = {
  createNewRequest: createNewRequestAsync.request,
  fetchCommunityCircles: fetchCommunityCirclesAsync.request,
};

type dispatchType = typeof mapDispatchToProps;

interface Props extends ReturnType<typeof mapStateToProps>, dispatchType {
  project: Project;
  bearer: string;
  buttonView?: boolean;
  defaultSummary?: string;
  defaultDescription?: string;
  community?: Community;
}

const CoachingSessionCard: React.FC<Props> = ({
  literals,
  project,
  bearer,
  createNewRequest,
  buttonView,
  selectedCommunity,
  fetchCommunityCircles,
  community,
  ...props
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [teamMembers, setTeamMembers] = useState<Member[]>(project.members || []);

  const teamCircles = useMemo(() => (project.circles || []).filter((c: Circle) => c.name === 'Team'), [
    project.circles,
  ]);

  const coachingModalProjectCircles = useMemo(
    () => (project?.circles || []).filter((c: Circle) => ['Team', 'Mentors'].includes(c.name)),
    [project?.circles],
  );

  const communityCircles = useMemo(() => community?.circles || selectedCommunity?.circles || [], [
    community?.circles,
    selectedCommunity?.circles,
  ]);

  const filteredTeamMembers = useMemo(
    () =>
      (teamCircles.length === 1 && teamMembers.filter((member: Member) => member.circle.id === teamCircles[0].id)) ||
      [],
    [teamCircles, teamMembers],
  );

  const history = useHistory();
  const addRequestModal = (history.location.state as any)?.addRequestModal || false;

  const fetchMembersAddRequestModal = async (circleId: number, skip: number) => {
    if (!selectedCommunity && !community) return [];
    const communityId = community?.id || selectedCommunity?.id || 0;
    const members = await fetchCommunityMembersByCircle(bearer, communityId, circleId, skip, TAKE);
    return members.members;
  };

  const fetchTeamAddRequestModal = async () => {
    if (teamMembers.length) return teamMembers;
    if (!project) return [];
    const members = await fetchProjectMembers(bearer, project.id, {});
    setTeamMembers(members.members);
    return members.members;
  };

  const fetchCircles = () => {
    if (!selectedCommunity && !community) return;
    const communityId = community?.id || selectedCommunity?.id || 0;
    fetchCommunityCircles({ bearer, communityId });
  };

  return (
    <>
      {addRequestModal && (
        <AddCoachingModal
          projectId={project.id}
          circles={communityCircles}
          teamCircles={coachingModalProjectCircles}
          fetchMembers={fetchMembersAddRequestModal}
          fetchTeam={fetchTeamAddRequestModal}
          fetchCircles={fetchCircles}
          searchMembers={(concept: string): Promise<{ list: Member[] }> => {
            return new Promise((resolve, reject) => {
              const communityId = community?.id || selectedCommunity?.id || 0;
              fetchCommunityMembers(bearer, communityId, {}, { concept, name: concept }, 0, 30, false)
                .then((response: { list: CommunityMembers }) => resolve({ list: response.list.members || [] }))
                .catch(reject);
            });
          }}
          teamMemberScheduling={project.isProjectAdmin || false}
          forceShowCoachingModal
        />
      )}
      {buttonView ? (
        <StyledButton
          onClick={() => {
            createNewRequest({ projectId: project.id, isProjectAdmin: true });
          }}
        >
          {literals.coaching_schedule_session}
        </StyledButton>
      ) : (
        <Card>
          <Image src={'/static/coaching-session/coach.png'} alt={literals.resources_upload_resources} />
          <StyledCardDescription>{literals.coaching_session_panel_description}</StyledCardDescription>
          <StyledButton
            onClick={() => {
              createNewRequest({ projectId: project.id, isProjectAdmin: true });
            }}
          >
            {literals.coaching_schedule_session}
          </StyledButton>
        </Card>
      )}
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CoachingSessionCard);
